import { createContext, useContext } from 'react';
import { resolveDrop, type DragSource, type DropTarget } from '../../model/drop-rules';
import { useWorkspace } from '../../state/workspace-context';

/** Origen del arrastre en curso (`null` si no se arrastra nada). Estado de UI, no de documento. */
export const ActiveDragContext = createContext<DragSource | null>(null);

export const useActiveDrag = (): DragSource | null => useContext(ActiveDragContext);

/**
 * Validez del destino para el arrastre en curso: `null` sin arrastre, `true`/`false` segun
 * las reglas de drop. Permite resaltar las zonas validas antes de soltar.
 */
export function useDropValidity(target: DropTarget): boolean | null {
  const source = useActiveDrag();
  const { document, catalog } = useWorkspace();
  if (source === null) return null;
  return resolveDrop(source, target, document, catalog).ok;
}

/** Clases de feedback comunes a todas las zonas de destino. */
export function dropFeedbackClass(valid: boolean | null, isOver: boolean): string {
  if (valid === null) return '';
  if (isOver) {
    return valid ? 'ring-2 ring-primary bg-primary/5' : 'ring-2 ring-destructive bg-destructive/5';
  }
  return valid ? 'ring-1 ring-primary/40' : 'opacity-60';
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** Lee el origen tipado de los datos de dnd-kit (que son `any`). */
export function readDragSource(data: unknown): DragSource | null {
  if (!isObject(data) || !isObject(data.source) || typeof data.source.type !== 'string') {
    return null;
  }
  return data.source as unknown as DragSource;
}

export function readDropTarget(data: unknown): DropTarget | null {
  if (!isObject(data) || !isObject(data.target) || typeof data.target.type !== 'string') {
    return null;
  }
  return data.target as unknown as DropTarget;
}
